import { Alert, Button, Card, Progress, Space, Spin, Tooltip, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Graph from '../components/Graph';
import StatisticalExportButton from '../components/StatisticalExportButton';
import Chat from '../components/Chat';
import CopyViewLinkButton from '../components/CopyViewLinkButton';
import useShowContext from '../hooks/useShowContext';
import { releaseGraph, retrieveGraph, getStatisticalExportStatus } from '../services/api';
import './query.css';

const { Title, Paragraph } = Typography;

export default function QueryPage({
  graphId: graphIdProp,
  graphTitle: graphTitleProp,
  initialChatModel: initialChatModelProp,
}) {
  const { graphId: graphAddressId } = useParams();
  const [graphDetails, setGraphDetails] = useState(() => (
    graphAddressId
      ? null
      : {
          id: graphIdProp,
          title: graphTitleProp,
          chatModel: initialChatModelProp,
        }
  ));
  const [graphLoading, setGraphLoading] = useState(Boolean(graphAddressId));
  const [graphLoadError, setGraphLoadError] = useState('');
  const [statisticsStatus, setStatisticsStatus] = useState(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [dataIsV2, setDataIsV2] = useState(false);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [mobileChatOpen, setMobileChatOpen] = useState(false);
  const [isCompact, setIsCompact] = useState(() => window.matchMedia('(max-width: 900px)').matches);
  const isV2 = graphDetails?.extractionVersion === 'v2' || dataIsV2;
  const [contextCommunities, setContextCommunities] = useState([]);
  const { triggerSignal, sendTriggerSignal } = useShowContext();
  const graphId = graphDetails?.id || null;
  const graphTitle = graphDetails?.title || '';
  const initialChatModel = graphDetails?.chatModel || 'gpt-6-astra';
  const statsOnly = statisticsStatus?.mode === 'stats_only';
  const graphReady = !graphAddressId || graphDetails?.status === 'ready';
  const previewReady = Boolean(statisticsStatus?.preview_ready);
  const showPreview = !graphReady && previewReady;

  useEffect(() => {
    setStatisticsStatus(null);
    if (!graphId || !isV2) return undefined;
    let cancelled = false;
    let timer;
    const refresh = async () => {
      try {
        const status = await getStatisticalExportStatus(graphId);
        if (cancelled) return;
        setStatisticsStatus(status);
        if (!status.preview_ready && !['ready','failed'].includes(graphDetails?.status)) timer = setTimeout(refresh, 5000);
      } catch (error) { if (!cancelled) timer = setTimeout(refresh, 10000); }
    };
    refresh();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [graphId, isV2, graphDetails?.status]);

  useEffect(() => {
    if (!graphAddressId) {
      setGraphDetails({
        id: graphIdProp,
        title: graphTitleProp,
        chatModel: initialChatModelProp,
      });
      setGraphLoading(false);
      setGraphLoadError('');
      return undefined;
    }

    let cancelled = false;
    setGraphDetails(null);
    setGraphLoading(true);
    setGraphLoadError('');

    let refreshTimer;
    const loadGraph = async () => {
      try {
        const jobs = await retrieveGraph(graphAddressId);
        if (cancelled) return;
        const graph = jobs[0];
        if (!graph) throw new Error('Graph not found.');
        setGraphDetails({
          id: graph.id,
          uuid: graph.uuid,
          title: graph.title,
          chatModel: graph.chat_model || 'gpt-6-astra',
          extractionVersion: graph.extraction_version,
          status: graph.status,
          progress: graph.progress,
          percentComplete: graph.percent_complete,
        });
        setGraphLoadError('');
        if (['running', 'scheduled', 'importing'].includes(graph.status)) {
          refreshTimer = setTimeout(loadGraph, 5000);
        }
      } catch (error) {
        if (!cancelled) setGraphLoadError(error.message || 'Unable to load this graph.');
      } finally {
        if (!cancelled) setGraphLoading(false);
      }
    };
    loadGraph();

    return () => {
      cancelled = true;
      clearTimeout(refreshTimer);
    };
  }, [graphAddressId, graphIdProp, graphTitleProp, initialChatModelProp]);

  useEffect(() => { setDataIsV2(false); setMobileChatOpen(false); setDetailDrawerOpen(false); }, [graphId]);
  useEffect(() => {
    const media = window.matchMedia('(max-width: 900px)');
    const update = () => setIsCompact(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  useEffect(() => {
    if (!isV2) return undefined;
    const shell = document.querySelector('.app-shell');
    shell?.classList.add('app-shell--v2-workspace');
    return () => shell?.classList.remove('app-shell--v2-workspace');
  }, [isV2]);

  // Release the backend's cached load_params when the user leaves this graph
  // (route change, switching to a different graph, or closing the tab).
  useEffect(() => {
    if (!graphId) return undefined;
    const handleUnload = () => releaseGraph(graphId);
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      releaseGraph(graphId);
    };
  }, [graphId]);

  return (
    <div className={isV2 ? 'query-workspace-v2' : 'query-workspace'}>
      {isV2 ? (
        <div className="query-v2-titlebar">
          <Title level={4} title={graphTitle}>{graphTitle || 'Graph workspace'}</Title>
          <Space size={8} wrap className="query-v2-actions">
            {graphId && <StatisticalExportButton graphId={graphId} jobStatus={graphDetails?.status} size="small" />}
            <Tooltip title="Copy a link with the current graph view, filters and selected node or edge">
              <CopyViewLinkButton />
            </Tooltip>
            <Tooltip title="Click a node to focus its connected graph. Click an edge for evidence. Reset graph restores the full network. Search by name or node:0 / link:1 / comm:0.">
              <Button size="small" aria-label="Graph interaction help">Help</Button>
            </Tooltip>
            <Button size="small" onClick={() => isCompact ? setMobileChatOpen(value => !value) : setChatCollapsed(value => !value)}>
              {isCompact ? (mobileChatOpen ? 'Back to graph' : 'Open chat') : (chatCollapsed ? 'Show chat' : 'Hide chat')}
            </Button>
          </Space>
        </div>
      ) : <Card bordered={false} className="page-card">
        <Title level={3} className="page-section-title">
          {graphTitle || 'Graph query studio'}
        </Title>
        <Paragraph className="page-section-copy">
          Left click a node to check all neighboring nodes.
          Right click a node to check details.
          Search keywords to find relevant content.
          Seach 'node:0' 'link:1' or 'comm:0' to find graph elements by id.
        </Paragraph>
        {graphLoading && <Spin description="Loading graph..." />}
        {!graphLoading && graphLoadError && (
          <Alert
            showIcon
            type="error"
            message="Unable to open graph"
            description={graphLoadError}
          />
        )}
        {!graphLoading && !graphLoadError && !graphId && (
          <Alert
            showIcon
            type="info"
            message="No graph selected"
            description="Open a ready job from the Jobs page to load a graph into the query workspace."
          />
        )}
      </Card>}


      {graphId && !graphReady && (
        <Card bordered={false} className="page-card">
          <Alert
            showIcon
            type={statsOnly ? (statisticsStatus.error ? 'error' : statisticsStatus.complete ? 'success' : 'info') : graphDetails?.status === 'failed' ? 'error' : 'info'}
            message={previewReady && !statsOnly ? 'Statistical preview ready; full graph generation continues' : statsOnly ? (statisticsStatus.complete ? 'Statistical extraction complete' : 'Extracting statistical relationships') : graphDetails?.status === 'failed' ? 'Graph generation failed' : 'Graph is being generated'}
            description={statsOnly ? statisticsStatus.stage : graphDetails?.progress || 'The graph will load when extraction is complete.'}
          />
          <Progress
            percent={statsOnly ? Math.round(100 * statisticsStatus.completed_papers / statisticsStatus.total_papers) : graphDetails?.percentComplete || 0}
            status={statsOnly ? (statisticsStatus.error ? 'exception' : statisticsStatus.complete ? 'success' : 'active') : graphDetails?.status === 'failed' ? 'exception' : 'active'}
          />
        </Card>
      )}

      {graphId && (graphReady || previewReady) && <div className={`query-layout${isV2 ? ' query-layout--v2' : ''}${chatCollapsed || showPreview || statsOnly ? ' query-layout--chat-collapsed' : ''}${mobileChatOpen ? ' query-layout--mobile-chat' : ''}${detailDrawerOpen ? ' query-layout--detail-open' : ''}`}>
        <Card bordered={false} className="page-card query-panel query-panel--graph">
          <Graph
            graphId={graphId}
            preview={showPreview}
            statsOnly={statsOnly}
            graphUuid={graphDetails?.uuid}
            onVersionChange={setDataIsV2}
            onDrawerOpenChange={setDetailDrawerOpen}
            triggerSignal={triggerSignal}
            contextCommunities={contextCommunities}
          />
        </Card>
        {graphReady && !statsOnly && <Card bordered={false} className="page-card query-panel query-panel--chat">
          <Chat
            graphId={graphId}
            initialChatModel={initialChatModel}
            sendTriggerSignal={sendTriggerSignal}
            setContextCommunities={setContextCommunities}
          />
        </Card>}
      </div>}
    </div>
  );
}
